import React from 'react';
import { CrudPage } from '../../components/CrudPage';
import { type ProcessingLineSetupTimeDto, ProcessingLineSetupTimeControllerService } from '../../api';
import ProcessingLineSetupTimeFormDialog, { type ProcessingLineSetupTimeWithId } from '../forms/ProcessingLineSetupTimeFormDialog';
import { ResourceTypeControllerService, type ResourceTypeDto } from '../../api';
import { ResourceControllerService, type ResourceDto } from '../../api';
import { MaterialControllerService, type MaterialDto } from '../../api';

const ProcessingLineSetupTimesPage = () => {
  // Cache for resource types, resources, and products
  const [resourceTypesCache, setResourceTypesCache] = React.useState<Map<string, ResourceTypeDto>>(new Map());
  const [resourcesCache, setResourcesCache] = React.useState<Map<string, ResourceDto>>(new Map());
  const [productsCache, setProductsCache] = React.useState<Map<string, MaterialDto>>(new Map());

  // Load caches
  React.useEffect(() => {
    const loadCaches = async () => {
      try {
        // Load resource types
        const allResourceTypes = await ResourceTypeControllerService.findAll();
        const processingLineTypes = allResourceTypes.filter(rt => rt.category === 'PROCESSING_LINE');
        const rtMap = new Map<string, ResourceTypeDto>();
        processingLineTypes.forEach(rt => {
          if (rt.uuid) rtMap.set(rt.uuid, rt);
        });
        setResourceTypesCache(rtMap);

        // Load resources
        const allResources = await ResourceControllerService.findAll();
        const rMap = new Map<string, ResourceDto>();
        allResources.forEach(r => {
          if (r.uuid) rMap.set(r.uuid, r);
        });
        setResourcesCache(rMap);

        // Load products
        const allMaterials = await MaterialControllerService.findAll();
        const productMaterials = allMaterials.filter(m => m.materialType === 'PRODUCT');
        const pMap = new Map<string, MaterialDto>();
        productMaterials.forEach(p => {
          if (p.uuid) pMap.set(p.uuid, p);
        });
        setProductsCache(pMap);
      } catch (error) {
        console.error('Error loading caches:', error);
      }
    };
    loadCaches();
  }, []);

  // Renderer for resource type
  const ResourceTypeRenderer = (data: any) => {
    const item = data?.data || data?.rowData || data;
    const resourceTypeUuid = item?.resourceTypeUuid;
    if (!resourceTypeUuid) return '';
    const resourceType = resourceTypesCache.get(resourceTypeUuid);
    return resourceType?.name || resourceTypeUuid;
  };

  // Renderer for resource
  const ResourceRenderer = (data: any) => {
    const item = data?.data || data?.rowData || data;
    const resourceUuid = item?.resourceUuid;
    if (!resourceUuid) return '';
    const resource = resourcesCache.get(resourceUuid);
    return resource?.name || resourceUuid;
  };

  // Renderer for product
  const ProductRenderer = (data: any) => {
    const item = data?.data || data?.rowData || data;
    const productUuid = item?.productUuid;
    if (!productUuid) return '';
    const product = productsCache.get(productUuid);
    return product?.name || productUuid;
  };

  // Helper function to format seconds as HH:MM:SS
  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Renderer for setup time
  const SetupTimeRenderer = (data: any) => {
    const item = data?.data || data?.rowData || data;
    const seconds = item?.setupTimeSeconds;
    if (seconds === null || seconds === undefined) {
      return <span className="text-gray-500">-</span>;
    }
    return (
      <span className="text-gray-900">{formatDuration(seconds)}</span>
    );
  };

  const fetchData = async (): Promise<ProcessingLineSetupTimeWithId[]> => {
    try {
      const data = await ProcessingLineSetupTimeControllerService.findAll();
      const mappedData = data.map(item => ({
        ...item,
        id: item.uuid || ''
      }));
      return mappedData;
    } catch (error: any) {
      console.error('ProcessingLineSetupTimesPage: Error fetching setup times:', error);
      throw error;
    }
  };

  return (
    <CrudPage<ProcessingLineSetupTimeWithId>
      titleKey="headers.processingLineSetupTimes"
      gridMeta={[
        { field: 'resourceTypeUuid', headerKey: 'processingLineSetupTimes.grid.field.resourceType', width: 200, render: ResourceTypeRenderer },
        { field: 'resourceUuid', headerKey: 'processingLineSetupTimes.grid.field.resource', width: 200, render: ResourceRenderer },
        { field: 'productUuid', headerKey: 'processingLineSetupTimes.grid.field.product', width: 200, render: ProductRenderer },
        { field: 'setupTimeSeconds', headerKey: 'processingLineSetupTimes.grid.field.setupTime', width: 150, align: 'Center', render: SetupTimeRenderer }
      ]}
      fetchData={fetchData}
      saveData={async (data: ProcessingLineSetupTimeWithId) => {
        try {
          const apiData: ProcessingLineSetupTimeDto = {
            ...data,
            uuid: data.id || undefined
          };
          delete (apiData as any).id;

          if (data.id) {
            const updated = await ProcessingLineSetupTimeControllerService.update(data.id, apiData);
            return {
              ...updated,
              id: updated.uuid || data.id
            };
          } else {
            const created = await ProcessingLineSetupTimeControllerService.create(apiData);
            return {
              ...created,
              id: created.uuid || ''
            };
          }
        } catch (error: any) {
          console.error('ProcessingLineSetupTimesPage: Error saving setup time:', error);
          throw error;
        }
      }}
      deleteData={(id: string) => ProcessingLineSetupTimeControllerService.delete(id)}
      FormComponent={ProcessingLineSetupTimeFormDialog}
      entityName="processinglinesetuptime"
    />
  );
};

export default ProcessingLineSetupTimesPage;
